import { useEffect, useMemo, useState } from "react";
import { Search, ChevronDown } from "lucide-react";
import { apiMarketPairs, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * MarketPicker — searchable dropdown of Binance Spot trading pairs.
 * Loads the full list once (backend caches for 1h).
 */
export default function MarketPicker({ value, onChange }) {
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const d = await apiMarketPairs({ exchange: "binance", market_type: "spot" });
        setPairs(d.pairs || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toUpperCase();
    if (!term) return pairs.slice(0, 200);
    return pairs.filter((p) =>
      p.symbol.includes(term) || p.base.includes(term) || p.quote.includes(term)
    ).slice(0, 200);
  }, [q, pairs]);

  return (
    <div className="relative" data-testid="market-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="market-picker-trigger"
        className="inline-flex items-center gap-2 min-w-[180px] border border-neutral-800 hover:border-neutral-600 bg-neutral-950 text-white font-mono text-sm px-3 py-2 transition-colors"
      >
        <span className="tabular-nums">{value || "SELECT PAIR"}</span>
        <ChevronDown className="w-3.5 h-3.5 ml-auto opacity-60" />
      </button>

      {open && (
        <div
          className="absolute z-40 mt-1 w-[280px] border border-neutral-800 bg-[#0a0a0a] shadow-lg"
          data-testid="market-picker-panel"
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-900">
            <Search className="w-3.5 h-3.5 text-neutral-500" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search e.g. BTC, ETHUSDT…"
              data-testid="market-picker-search"
              className="flex-1 bg-transparent text-white text-xs font-mono outline-none placeholder:text-neutral-600"
            />
          </div>
          <div className="max-h-[320px] overflow-y-auto" data-testid="market-picker-list">
            {loading && (
              <div className="px-3 py-4 text-xs font-mono uppercase tracking-widest text-neutral-500">loading pairs…</div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="px-3 py-4 text-xs font-mono uppercase tracking-widest text-neutral-500">no matches</div>
            )}
            {!loading && filtered.map((p) => (
              <button
                key={p.symbol}
                type="button"
                onClick={() => { onChange(p.symbol); setOpen(false); setQ(""); }}
                data-testid={`market-picker-option-${p.symbol}`}
                className={`w-full text-left px-3 py-2 text-xs font-mono flex items-center justify-between gap-2 hover:bg-neutral-900 ${
                  p.symbol === value ? "bg-neutral-900 text-[#00E5FF]" : "text-neutral-200"
                }`}
              >
                <span className="tabular-nums">{p.symbol}</span>
                <span className="text-neutral-600">{p.base}/{p.quote}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
